/**
 * AccountablePicker — M-Rhythm-1
 *
 * Polymorphic single-select picker for an "accountable" relationship.
 * Used in: cadence defaults, meeting accountable, agenda item accountable,
 * domain accountable column in Structure Settings → Domains tab.
 *
 * Two modes:
 *   - 'actor-only'    — renders just an actor dropdown (no domain toggle).
 *   - 'polymorphic'   — renders a toggle ("Actor" / "Domain") plus the
 *                       corresponding dropdown. At most one of actorId /
 *                       domainId is non-null (XOR invariant mirrors the DB).
 *
 * The component is uncontrolled-by-default: callers supply `value` + `onChange`.
 */

import React, { useState } from 'react';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input/Input';
import { User, Globe } from 'lucide-react';
import { t } from '@/lib/i18n';
import type { RorosActorDTO } from '@/types/roros/actors';
import type { RorosDomainDTO } from '@/types/roros/domains';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface AccountableValue {
  actorId: string | null;
  domainId: string | null;
}

export interface AccountablePickerProps {
  /** Current selection. Either actorId or domainId should be set, not both. */
  value: AccountableValue;
  onChange: (next: AccountableValue) => void;
  /** List of actors to display in the actor dropdown. */
  actors: RorosActorDTO[];
  /**
   * List of domains to display in the domain dropdown.
   * Only used when mode === 'polymorphic'.
   */
  domains?: RorosDomainDTO[];
  /**
   * 'actor-only'  — no domain option; actor dropdown only.
   * 'polymorphic' — toggle between actor and domain, with respective dropdown.
   * @default 'actor-only'
   */
  mode?: 'actor-only' | 'polymorphic';
  /** Whether the picker is disabled. */
  disabled?: boolean;
  /** Optional actor search value. Used by callers that search actors server-side. */
  actorSearchValue?: string;
  /** Optional actor search setter. When present, an actor search field is rendered. */
  onActorSearchChange?: (value: string) => void;
  /** Shows a small loading hint while actor search is refreshing. */
  actorSearchLoading?: boolean;
  /** Placeholder shown when nothing is selected. */
  placeholder?: string;
  className?: string;
  'data-testid'?: string;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const UNSET = '__unset__';

function actorLabel(actor: RorosActorDTO): string {
  return actor.name;
}

// ─── Component ─────────────────────────────────────────────────────────────

export function AccountablePicker({
  value,
  onChange,
  actors,
  domains = [],
  mode = 'actor-only',
  disabled = false,
  actorSearchValue = '',
  onActorSearchChange,
  actorSearchLoading = false,
  placeholder,
  className,
  'data-testid': testId,
}: AccountablePickerProps): React.JSX.Element {
  // In polymorphic mode, determine the current tab from the value.
  // If neither is set, default to 'actor'.
  const [tab, setTab] = useState<'actor' | 'domain'>(
    value.domainId && mode === 'polymorphic' ? 'domain' : 'actor',
  );

  const effectivePlaceholder = placeholder ?? t('None');

  const actorOptions = [
    { value: UNSET, label: effectivePlaceholder },
    ...actors.map((a) => ({ value: a.id, label: actorLabel(a) })),
  ];

  // C10: Only show active domains. Inactive domains must not appear as options.
  const activeDomains = domains.filter((d) => d.isActive);
  const domainOptions = [
    { value: UNSET, label: effectivePlaceholder },
    ...activeDomains.map((d) => ({ value: d.id, label: d.name })),
  ];

  const handleTabSwitch = (newTab: 'actor' | 'domain') => {
    if (newTab === tab) return;
    setTab(newTab);
    // Clear both when switching — avoids stale other-type value.
    onChange({ actorId: null, domainId: null });
  };

  const handleActorChange = (val: string) => {
    onChange({ actorId: val === UNSET ? null : val, domainId: null });
  };

  const handleDomainChange = (val: string) => {
    onChange({ actorId: null, domainId: val === UNSET ? null : val });
  };

  const currentActorVal = value.actorId ?? UNSET;
  const currentDomainVal = value.domainId ?? UNSET;
  const actorSearchInput = onActorSearchChange && !disabled ? (
    <div className="space-y-1">
      <Input
        type="text"
        value={actorSearchValue}
        onChange={(event) => onActorSearchChange(event.target.value)}
        placeholder={t('Search actors or domains...')}
        aria-label={t('Search actors or domains')}
        className="w-full text-sm"
        data-testid={testId ? `${testId}-actor-search` : undefined}
      />
      {actorSearchLoading && (
        <p className="text-xs text-muted-foreground">{t('Loading...')}</p>
      )}
    </div>
  ) : null;

  if (mode === 'actor-only') {
    if (actorSearchInput) {
      return (
        <div className={className}>
          {actorSearchInput}
          <Select
            value={currentActorVal}
            onValueChange={handleActorChange}
            options={actorOptions}
            disabled={disabled}
            className="w-full"
            data-testid={testId}
            aria-label={t('Accountable actor')}
          />
        </div>
      );
    }

    return (
      <Select
        value={currentActorVal}
        onValueChange={handleActorChange}
        options={actorOptions}
        disabled={disabled}
        className={className}
        data-testid={testId}
        aria-label={t('Accountable actor')}
      />
    );
  }

  // Polymorphic mode
  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`} data-testid={testId}>
      {/* Tab toggle */}
      <div className="flex items-center rounded-md border border-border overflow-hidden shrink-0" role="tablist" aria-label={t('Accountable type')}>
        <Button
          type="button"
          variant={tab === 'actor' ? 'primary' : 'ghost'}
          size="sm"
          className="h-8 px-2 rounded-none border-0 flex items-center gap-1"
          disabled={disabled}
          onClick={() => handleTabSwitch('actor')}
          role="tab"
          aria-selected={tab === 'actor'}
          data-testid={testId ? `${testId}-actor-tab` : undefined}
        >
          <User className="h-3.5 w-3.5" />
          <span className="text-xs">{t('Actor')}</span>
        </Button>
        <div className="w-px h-full bg-border" />
        <Button
          type="button"
          variant={tab === 'domain' ? 'primary' : 'ghost'}
          size="sm"
          className="h-8 px-2 rounded-none border-0 flex items-center gap-1"
          disabled={disabled}
          onClick={() => handleTabSwitch('domain')}
          role="tab"
          aria-selected={tab === 'domain'}
          data-testid={testId ? `${testId}-domain-tab` : undefined}
        >
          <Globe className="h-3.5 w-3.5" />
          <span className="text-xs">{t('Domain')}</span>
        </Button>
      </div>

      {/* Dropdown for current tab */}
      {tab === 'actor' ? (
        <div className="min-w-0 flex-1 space-y-1">
          {actorSearchInput}
          <Select
            value={currentActorVal}
            onValueChange={handleActorChange}
            options={actorOptions}
            disabled={disabled}
            className="w-full"
            data-testid={testId ? `${testId}-actor-select` : undefined}
            aria-label={t('Accountable actor')}
          />
        </div>
      ) : (
        <Select
          value={currentDomainVal}
          onValueChange={handleDomainChange}
          options={domainOptions}
          disabled={disabled}
          className="min-w-0 flex-1"
          data-testid={testId ? `${testId}-domain-select` : undefined}
          aria-label={t('Accountable domain')}
        />
      )}
    </div>
  );
}
